import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import './OutstandingOverview.css';

/**
 * The district admin landing page: what still needs doing.
 *
 * Replaces a welcome banner. Two rules shaped this screen:
 *
 * 1. Never show a number staff would dispute. The counting rules and the deliberate
 *    exclusions live in backend/services/adminOutstanding.js. Per-form numbers appear as
 *    "received of cohort" coverage rather than as a backlog, because no district has
 *    marked any form required, so the app cannot claim a child owes one.
 *
 * 2. Never fail silently. A blank card on this page reads as "nothing to do", which is the
 *    one wrong answer it must not give. Any bucket the server could not compute is listed
 *    by name with its reason, and a failed load says so instead of rendering zeros.
 */

const SEVERITY_LABEL = {
  action: 'Needs staff action',
  waiting_on_parent: 'Waiting on a parent',
  integrity: 'Worth checking',
  queue: 'Working queue',
  ok: 'Clear'
};

// Buckets whose count opens a list. Keyed to the server's child-list buckets.
const CHILD_LIST_BUCKETS = new Set(['no_forms_received', 'forms_without_record']);

const OutstandingOverview = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [openBucket, setOpenBucket] = useState(null);
  const [listState, setListState] = useState({ loading: false, error: '', rows: [], truncated: false });

  // Per-form drill-down and "ask the parents" state, keyed by form instance id so two rows
  // never share one spinner.
  const [openForm, setOpenForm] = useState(null);
  const [formOwed, setFormOwed] = useState({ loading: false, error: '', rows: [], total: 0 });
  const [notifying, setNotifying] = useState(null);
  const [notifyResult, setNotifyResult] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await axios.get('/api/admin-overview');
      setData(res.data);
    } catch (error) {
      // Surface the server's reason. This page is about telling staff what is true.
      const reason = error.response?.data?.reason || error.response?.data?.error || error.message;
      setLoadError(reason || 'The overview could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openList = async (bucket) => {
    if (openBucket === bucket) { setOpenBucket(null); return; }
    setOpenBucket(bucket);
    setListState({ loading: true, error: '', rows: [], truncated: false });
    try {
      const url = bucket === 'unfinished_drafts'
        ? '/api/admin-overview/drafts'
        : `/api/admin-overview/children/${bucket}`;
      const res = await axios.get(url);
      setListState({
        loading: false,
        error: res.data.reason || '',
        rows: res.data.children || res.data.drafts || [],
        truncated: !!res.data.truncated
      });
    } catch (error) {
      const reason = error.response?.data?.reason || error.response?.data?.error || error.message;
      setListState({ loading: false, error: reason || 'The list could not be loaded.', rows: [], truncated: false });
    }
  };

  const openFormOwed = async (formId) => {
    if (openForm === formId) { setOpenForm(null); return; }
    setOpenForm(formId);
    setFormOwed({ loading: true, error: '', rows: [], total: 0 });
    try {
      const res = await axios.get(`/api/admin-overview/forms/${formId}/owed`);
      setFormOwed({
        loading: false,
        error: '',
        rows: res.data.children || [],
        total: res.data.total || 0
      });
    } catch (error) {
      const reason = error.response?.data?.reason || error.response?.data?.error || error.message;
      setFormOwed({ loading: false, error: reason || 'The list could not be loaded.', rows: [], total: 0 });
    }
  };

  /**
   * Tell every parent who owes this form that it is now required.
   *
   * Confirmed first: it is the only button on this page that sends anything to a family, and
   * the count in the message is what staff are agreeing to. Re-pressing is safe - the server
   * skips anyone already notified - but a silent double send would still read as a mistake.
   */
  const notifyParents = async (formId, formName, owed) => {
    const ok = window.confirm(
      `Tell parents that ${formName} is now required?\n\n` +
      `${owed} ${owed === 1 ? 'child owes' : 'children owe'} this form, but only those whose ` +
      'enrollment is already submitted will be told - anyone still to enrol will get the form ' +
      'in their packet anyway.\n\n' +
      'They will see it on their dashboard next time they sign in. No email is sent, and nothing ' +
      'they have already submitted is sent back or reopened.'
    );
    if (!ok) return;

    setNotifying(formId);
    try {
      const res = await axios.post(`/api/admin-overview/forms/${formId}/notify`, {});
      setNotifyResult((prev) => ({ ...prev, [formId]: { ok: true, ...res.data } }));
      load();
    } catch (error) {
      const reason = error.response?.data?.reason || error.response?.data?.error || error.message;
      setNotifyResult((prev) => ({ ...prev, [formId]: { ok: false, error: reason || 'Could not send.' } }));
    } finally {
      setNotifying(null);
    }
  };

  if (loading) {
    return <div className="outstanding"><p className="outstanding-muted">Loading…</p></div>;
  }

  if (loadError) {
    return (
      <div className="outstanding">
        <h2>{user?.district_name || 'District'}</h2>
        <div className="outstanding-degraded">
          <strong>The overview could not be loaded.</strong>
          <p>{loadError}</p>
          <button type="button" className="outstanding-retry" onClick={load}>Try again</button>
        </div>
      </div>
    );
  }

  const buckets = data?.buckets || [];
  // Cards are for work. Anything at zero collapses into one line underneath, so six cards
  // do not compete for attention when only three of them have anything in them.
  const active = buckets.filter((b) => b.count > 0);
  const clear = buckets.filter((b) => b.count === 0);
  const coverage = data?.coverage || [];
  const degraded = data?.degraded || [];
  const cohort = data?.cohort;

  return (
    <div className="outstanding">
      <div className="outstanding-head">
        <div>
          <h2>What needs attention</h2>
          <p className="outstanding-muted">
            {user?.district_name || 'District'}
            {data?.school_year ? ` · ${data.school_year} enrollment` : ''}
          </p>
        </div>
        <button type="button" className="outstanding-retry" onClick={load}>Refresh</button>
      </div>

      {degraded.length > 0 && (
        <div className="outstanding-degraded">
          <strong>
            {degraded.length === 1
              ? 'One part of this page could not be calculated.'
              : `${degraded.length} parts of this page could not be calculated.`}
          </strong>
          <ul>
            {degraded.map((d) => (
              <li key={d.bucket}><code>{d.bucket}</code> — {d.reason}</li>
            ))}
          </ul>
          <p className="outstanding-muted">
            Everything else on this page is still accurate. This has also been written to the server log.
          </p>
        </div>
      )}

      {cohort && (
        <p className="outstanding-cohort">
          <strong>{cohort.children}</strong> children listed by parents for this year ·{' '}
          <strong>{cohort.with_any_form}</strong> have sent at least one form ·{' '}
          <strong>{cohort.with_enrollment_record}</strong> have an enrollment record
        </p>
      )}

      {buckets.length === 0 && degraded.length === 0 && (
        <p className="outstanding-muted">Nothing to report for this year.</p>
      )}

      {active.length === 0 && buckets.length > 0 && (
        <p className="outstanding-allclear">Nothing needs attention right now.</p>
      )}

      <div className="outstanding-cards">
        {active.map((b) => {
          const clickable = CHILD_LIST_BUCKETS.has(b.key) || b.key === 'unfinished_drafts';
          const isOpen = openBucket === b.key;
          return (
            <div key={b.key} className={`outstanding-card sev-${b.severity}`}>
              <div className="outstanding-card-top">
                <span className="outstanding-count">{b.count}{b.of ? <span className="outstanding-of"> of {b.of}</span> : null}</span>
                <span className="outstanding-sev">{SEVERITY_LABEL[b.severity] || b.severity}</span>
              </div>
              <h3>{b.label}</h3>
              <p>{b.detail}</p>
              <div className="outstanding-card-actions">
                {clickable && b.count > 0 && (
                  <button type="button" className="outstanding-linkish" onClick={() => openList(b.key)}>
                    {isOpen ? 'Hide list' : 'Show list'}
                  </button>
                )}
                {b.link && <Link to={b.link}>Open screen</Link>}
              </div>
              {isOpen && (
                <div className="outstanding-list">
                  {listState.loading && <p className="outstanding-muted">Loading list…</p>}
                  {listState.error && <p className="outstanding-error">{listState.error}</p>}
                  {!listState.loading && !listState.error && listState.rows.length === 0 && (
                    <p className="outstanding-muted">Nothing in this list.</p>
                  )}
                  {listState.rows.length > 0 && (
                    <>
                      {listState.truncated && (
                        <p className="outstanding-muted">Showing the first {listState.rows.length}. Use the screen link for the full list.</p>
                      )}
                      <table>
                        <thead>
                          {b.key === 'unfinished_drafts' ? (
                            <tr><th>Form</th><th>Parent</th><th>Progress</th><th>Last saved</th></tr>
                          ) : (
                            <tr><th>Child</th><th>Grade</th><th>Parent</th><th>Contact</th></tr>
                          )}
                        </thead>
                        <tbody>
                          {listState.rows.map((r) => (
                            b.key === 'unfinished_drafts' ? (
                              <tr key={r.id}>
                                <td>{String(r.form_name || '').replace(/^.*?\s-\s/, '')}</td>
                                <td>{r.parent_name || '—'}</td>
                                <td>{r.progress_percentage != null ? `${r.progress_percentage}%` : '—'}</td>
                                <td>{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : '—'}</td>
                              </tr>
                            ) : (
                              <tr key={r.id}>
                                <td>{r.first_name} {r.last_name}</td>
                                <td>{r.grade || <span className="outstanding-error">missing</span>}</td>
                                <td>{r.parent_name || '—'}</td>
                                <td>{r.parent_email
                                  ? <a href={`mailto:${r.parent_email}`}>{r.parent_email}</a>
                                  : '—'}</td>
                              </tr>
                            )
                          ))}
                        </tbody>
                      </table>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {clear.length > 0 && active.length > 0 && (
        <p className="outstanding-clear-line">
          <strong>Nothing outstanding:</strong>{' '}
          {clear.map((b, i) => (
            <span key={b.key}>
              {/* Separated with a middot, not a comma: one of these labels contains a comma
                  of its own ("Forms sent back, waiting on a parent"), so a comma-joined list
                  read as three items instead of two. */}
              {i > 0 ? ' · ' : ''}
              {b.label.toLowerCase()}
            </span>
          ))}
        </p>
      )}

      {coverage.length > 0 && (
        <div className="outstanding-coverage">
          <h3>Forms still owed</h3>
          <p className="outstanding-muted">
            Counted only for the children each form actually applies to — the same rule the
            parent’s own form list uses, so the Chromebook agreement is not counted for
            Preschool, and Early Dismissal is not counted for grades 7–12.
          </p>
          <table>
            <thead>
              <tr>
                <th>Form</th>
                <th>Applies to</th>
                <th>Received</th>
                <th>Still owed</th>
              </tr>
            </thead>
            <tbody>
              {coverage.map((c) => {
                /* The migrant questionnaire is counted here but is not a form_instance row -
                   its key is a slug, not an id - so it has no drill-down and nothing to send. */
                const isInstance = /^[0-9a-f]{8}-/i.test(String(c.key));
                const result = notifyResult[c.key];
                return (
                  <React.Fragment key={c.key}>
                    <tr>
                      <td>{c.form_name}</td>
                      <td>{c.applies}</td>
                      <td>{c.received}</td>
                      <td className={c.owed > 0 ? 'outstanding-owed' : 'outstanding-owed-clear'}>
                        {c.owed > 0 ? c.owed : '—'}
                        {isInstance && c.owed > 0 && (
                          <span className="outstanding-row-actions">
                            <button type="button" className="outstanding-linkish" onClick={() => openFormOwed(c.key)}>
                              {openForm === c.key ? 'Hide list' : 'Show list'}
                            </button>
                            <button
                              type="button"
                              className="outstanding-linkish"
                              disabled={notifying === c.key}
                              onClick={() => notifyParents(c.key, c.form_name, c.owed)}
                            >
                              {notifying === c.key ? 'Sending…' : 'Tell their parents'}
                            </button>
                          </span>
                        )}
                      </td>
                    </tr>

                    {result && (
                      <tr>
                        <td colSpan={4} className="outstanding-muted">
                          {result.ok
                            ? [
                                `${result.sent} notified`,
                                result.skipped ? `${result.skipped} already had a notice` : null,
                                result.notEnrolled
                                  ? `${result.notEnrolled} not yet enrolled, so the form is already in their packet`
                                  : null
                              ].filter(Boolean).join(' · ') + '.'
                            : result.error}
                        </td>
                      </tr>
                    )}

                    {openForm === c.key && (
                      <tr>
                        <td colSpan={4}>
                          {formOwed.loading && <p className="outstanding-muted">Loading…</p>}
                          {formOwed.error && <p className="outstanding-muted">{formOwed.error}</p>}
                          {!formOwed.loading && !formOwed.error && (
                            <ul className="outstanding-list">
                              {formOwed.rows.map((child) => (
                                <li key={child.student_intake_id}>
                                  <Link to={`/district-admin/edit-student/${child.student_intake_id}`}>
                                    {child.student_name || 'Unnamed child'}
                                  </Link>
                                  {child.grade ? ` · ${child.grade}` : ''}
                                </li>
                              ))}
                              {formOwed.rows.length === 0 && (
                                <li className="outstanding-muted">Nobody, as of this refresh.</li>
                              )}
                            </ul>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

    </div>
  );
};

export default OutstandingOverview;
